import React, { useContext, useEffect, useRef, useState } from 'react';
import { clinicInfo } from '../data';
import API from '../services/interceptor';
import { AuthContext } from '../context/AuthContext';

export default function AppointmentForm() {

  const { visitReason, getDropDownMenu } = useContext(AuthContext);

  const [form, setForm] = useState({
    name: '',
    dateOfBirth: '',
    gender: '',
    contact: {
      mobile: '',
      altMobile: '',
      email: '',
      address: ''
    },
    reason: '',
    age: '',
    notes: ''
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [showOtpBox, setShowOtpBox] = useState(false);
  const otpRefs = useRef([]);

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
    '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM'
  ];

  useEffect(() => {
    getDropDownMenu();
  }, []);

  const toLocalDay = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const isSameDay = (a, b) => {
    if (!a || !b) return false;
    const da = toLocalDay(a);
    const db = toLocalDay(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
  };

  const formatLocalYYYYMMDD = (date) => {
    if (!date) return null;
    const d = toLocalDay(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const daysOfWeek = () => {
    const today = new Date();
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const copy = new Date(base);
      copy.setDate(base.getDate() + i);
      arr.push(copy);
    }
    return arr;
  };

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [key]: value }));

    if (key === 'dateOfBirth') {
      if (!value) {
        setForm(prev => ({ ...prev, age: '' }));
        return;
      }
      const birthDate = new Date(value);
      const today = new Date();
      let years = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        years--;
      }
      setForm(prev => ({ ...prev, age: Number.isFinite(years) && years >= 0 ? years.toString() : '' }));
    }
  };

  const onContactChange = (contactKey) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, contact: { ...prev.contact, [contactKey]: value } }));
  };

  const onOtpChange = (index) => (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
    setOtp(prev => {
      const next = [...prev];
      next[index] = val;
      return next;
    });

    if (val && index < otp.length - 1) {
      const nextInput = otpRefs.current[index + 1];
      if (nextInput) nextInput.focus();
    }
    if (!val && index > 0) {
      const prevInput = otpRefs.current[index - 1];
      if (prevInput) prevInput.focus();
    }
  };

  const handleSendOTP = async () => {
    if (!form.name || !form.name.trim()) {
      alert('Please enter your name');
      return;
    }

    const mobile = (form.contact && form.contact.mobile) || '';
    const digits = mobile.replace(/\D/g, '');
    if (!digits || digits.length < 10) {
      alert('Please enter a valid 10-digit mobile number');
      return;
    }

    if (!selectedDate || !selectedTime) {
      alert('Please select date and time');
      return;
    }

    try {
      setLoading(true);
      const email = (form.contact && form.contact.email) || '';
      const res = await API.post(`/api/appointments/otp/send`, { mobile: digits, email });

      if (res?.data) {
        setOtpSent(true);
        setShowOtpBox(true);
        alert('OTP sent successfully! Check your phone/email.');
        setTimeout(() => {
          if (otpRefs?.current && otpRefs.current[0]) {
            otpRefs.current[0].focus();
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error sending OTP', err);
      const msg = err?.response?.data?.message || 'Failed to send OTP. Try again later.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAppointment = async () => {
    const otpValue = otp.join('');
    if (!otpValue || otpValue.length < 4) {
      alert('Please enter the complete 4-digit OTP');
      return;
    }

    const mobile = (form.contact && form.contact.mobile) || '';
    const payload = {
      name: form.name.trim(),
      mobile: mobile.replace(/\D/g, ''),
      altMobile: form.contact.altMobile || '',
      email: form.contact.email || '',
      address: form.contact.address || '',
      dateOfBirth: form.dateOfBirth || null,
      gender: form.gender || null,
      date: formatLocalYYYYMMDD(selectedDate),
      timeBlock: selectedTime,
      reason: form.reason || 'General',
      notes: form.notes || '',
      otp: otpValue
    };

    try {
      setLoading(true);
      const res = await API.post(`/api/appointments`, payload);

      if (res.status === 200 || res.status === 201) {
        alert('Appointment confirmed! ID: ' + (res.data?.appointmentId || 'N/A'));
        setForm({
          name: '',
          dateOfBirth: '',
          gender: '',
          contact: { mobile: '', altMobile: '', email: '', address: '' },
          reason: '',
          age: '',
          notes: ''
        });
        setSelectedDate(null);
        setSelectedTime(null);
        setOtp(['', '', '', '']);
        setOtpSent(false);
        setShowOtpBox(false);
      } else {
        alert(res.data?.message || 'Failed to create appointment.');
      }
    } catch (err) {
      console.error('Error creating appointment', err);
      const msg = err?.response?.data?.message || 'Error creating appointment. Try again later.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp(['', '', '', '']);
    await handleSendOTP();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-xl rounded-3xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 sm:p-6 text-white">
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Logo - Responsive sizing */}
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white backdrop-blur-sm flex items-center justify-center flex-shrink-0 text-white font-bold text-xl border-2 border-white/40">
                <img
                  src={clinicInfo.logo}
                  alt="Clinic Logo"
                  className="w-9 h-9 sm:w-11 sm:h-11 object-contain"
                />
              </div>

              {/* Text Content - Responsive typography */}
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-2xl font-bold truncate">
                  {clinicInfo.name}
                </h1>
                <p className="text-blue-100 text-xs sm:text-base line-clamp-2 sm:line-clamp-1">
                  {clinicInfo.address}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Book Your Appointment</h2>
              <p className="text-gray-600">Schedule your visit at your convenience</p>
            </div>

            <div className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  Personal Information
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Enter full name"
                    value={form.name}
                    onChange={onChange('name')}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                    <input
                      type="tel"
                      className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="+91 Enter mobile number"
                      value={form.contact.mobile}
                      onChange={onContactChange('mobile')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Alternate Mobile</label>
                    <input
                      type="tel"
                      className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="Alternate mobile"
                      value={form.contact.altMobile}
                      onChange={onContactChange('altMobile')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Enter email address"
                    value={form.contact.email}
                    onChange={onContactChange('email')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <input
                    className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Enter address"
                    value={form.contact.address}
                    onChange={onContactChange('address')}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <select
                      className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                      value={form.gender}
                      onChange={onChange('gender')}
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <input
                      type="date"
                      className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      value={form.dateOfBirth}
                      onChange={onChange('dateOfBirth')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                    <input
                      type="text"
                      placeholder="Auto-calculated"
                      className="w-full border border-gray-300 rounded-xl p-3 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      value={form.age}
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Visit</label>
                    <select
                      className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                      value={form.reason}
                      onChange={onChange('reason')}
                    >
                      {visitReason.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))
                      }
                      {console.log("visitReason", visitReason)}

                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    rows={3}
                    className="w-full border border-gray-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Any relevant notes (symptoms, previous history, etc.)"
                    value={form.notes}
                    onChange={onChange('notes')}
                  />
                </div>
              </div>

              {/* Date Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  Select Date & Time
                </h3>

                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Choose Date *</label>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {daysOfWeek().map((d) => {
                      const localD = toLocalDay(d);
                      const isSelected = selectedDate && isSameDay(selectedDate, localD);
                      const isToday = isSameDay(new Date(), localD);

                      return (
                        <button
                          type="button"
                          key={localD.toISOString()}
                          onClick={() => {
                            setSelectedDate(new Date(localD.getFullYear(), localD.getMonth(), localD.getDate()));
                            setSelectedTime(null);
                          }}
                          className={`min-w-[80px] p-4 rounded-xl text-center flex-shrink-0 transition-all ${isSelected
                            ? 'bg-blue-600 text-white shadow-lg scale-105'
                            : 'bg-white border-2 border-gray-200 hover:border-blue-300 hover:shadow-md'
                            }`}
                        >
                          <div className={`text-xs font-medium mb-1 ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                            {localD.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()}
                          </div>
                          <div className={`text-2xl font-bold mb-1 ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                            {localD.getDate()}
                          </div>
                          <div className={`text-xs ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                            {localD.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()}
                          </div>
                          {isToday && (
                            <div className={`text-xs mt-1 font-semibold ${isSelected ? 'text-yellow-300' : 'text-blue-600'}`}>
                              Today
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Choose Time Slot *</label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {timeSlots.map((time) => {
                        const isSelected = selectedTime === time;
                        return (
                          <button
                            type="button"
                            key={time}
                            onClick={() => setSelectedTime(time)}
                            className={`p-3 rounded-lg text-sm font-medium transition-all ${isSelected
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-white border border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                              }`}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>
                    {selectedTime && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          <span className="font-semibold">Selected:</span> {toLocalDay(selectedDate).toDateString()} at {selectedTime}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Send OTP Button */}
              {!otpSent && (
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              )}

              {/* OTP Verification Box */}
              {showOtpBox && (
                <div className="space-y-4 animate-fadeIn">
                  <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                    <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    Enter OTP
                  </h3>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                    <p className="text-sm text-gray-600 mb-4 text-center">
                      We've sent a 4-digit OTP to your mobile number
                    </p>

                    <div className="flex gap-3 justify-center mb-4">
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          id={`otp-${i}`}
                          ref={el => (otpRefs.current[i] = el)}
                          value={digit}
                          onChange={onOtpChange(i)}
                          inputMode="numeric"
                          maxLength={1}
                          className="w-14 h-14 border-2 border-gray-300 rounded-xl text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
                        />
                      ))}
                    </div>

                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={loading}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                      >
                        Resend OTP
                      </button>
                    </div>
                  </div>

                  {/* Confirm Appointment Button */}
                  <button
                    type="button"
                    onClick={handleConfirmAppointment}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 rounded-xl font-semibold text-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Confirming...' : 'Confirm Appointment'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          • Available slots from today for next 7 days
        </p>
      </div>
    </div>
  );
}